import Button from '@material-ui/core/Button';
import { ArrowBack } from '@material-ui/icons';
import { publicIconPath } from '@orientaction/utils';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { resetPassword } from '../../../../services/user.service';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import locale from './locale.json';

const PASSWORD_PATTERN =
  /^(((?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z]))).{8,}$/;

type ResetPasswordFields = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordForm = () => {
  const cls = useAuthStyles();
  const history = useHistory();
  const { search } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const { control, handleSubmit, watch } = useForm<ResetPasswordFields>({ mode: 'onChange' });
  const passwordRef = useRef('');
  passwordRef.current = watch('password', '');

  const onSubmit = async (data: ResetPasswordFields) => {
    setRegisterError('');
    setIsLoading(true);

    try {
      const code = new URLSearchParams(search).get('code');
      const response = await resetPassword({
        password: data.password,
        passwordConfirmation: data.password,
        code,
      });

      if (response?.data?.jwt) {
        localStorage.clear();
        history.push('/');
        return;
      }

      setRegisterError('Une erreur est survenue');
    } catch {
      setRegisterError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={cls.headingGroup}>
        <h1 className={cls.title}>{locale.title}</h1>
        <p className={cls.subtitle}>{locale.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={cls.form}>
        <div className={cls.fieldsGroup}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: PASSWORD_PATTERN,
                message:
                  'Votre mot de passe doit contenir au moins 8 caractères et au moins 3 des éléments suivants :\n- Lettres minuscules (a-z)\n- Lettres majuscules (A-Z)\n- Chiffres (0-9)\n- Caractères spéciaux (ex. !@#)}',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="reset-password" className={cls.field}>
                <span className={cls.label}>{locale.password}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={locale.passwordPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <button
                    type="button"
                    className={cls.inputIcon}
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={
                      showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                    }>
                    <RedesignImage
                      src={`${publicIconPath}/${showPassword ? 'EyeOpened' : 'EyeClosed'}.svg`}
                      alt=""
                    />
                  </button>
                </div>
                {fieldState.error?.message && (
                  <span className={`${cls.errorText} ${cls.errorTextMultiline}`}>
                    {fieldState.error.message}
                  </span>
                )}
              </label>
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            rules={{
              required: 'Champ obligatoire',
              validate: value =>
                value === passwordRef.current || "Le mot de passe n'est pas identique",
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="reset-confirm-password" className={cls.field}>
                <span className={cls.label}>{locale.confirmPassword}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="reset-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={locale.confirmPasswordPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <button
                    type="button"
                    className={cls.inputIcon}
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    aria-label={
                      showConfirmPassword
                        ? 'Masquer le mot de passe'
                        : 'Afficher le mot de passe'
                    }>
                    <RedesignImage
                      src={`${publicIconPath}/${
                        showConfirmPassword ? 'EyeOpened' : 'EyeClosed'
                      }.svg`}
                      alt=""
                    />
                  </button>
                </div>
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />
        </div>

        {registerError && <p className={cls.errorTextMd}>{registerError}</p>}

        <Button type="submit" className={cls.primaryButton} disabled={isLoading}>
          {isLoading ? `${locale.sendText}...` : locale.sendText}
        </Button>
      </form>

      <button type="button" className={cls.backButton} onClick={() => history.push('/')}>
        <ArrowBack className={cls.backIcon} />
        <span>{locale.backText}</span>
      </button>

      <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
        <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
        <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
      </button>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default ResetPasswordForm;
